import { Controller } from "@hotwired/stimulus"

// Two modes:
//
// 1. New sequence figures with `data-reveal-target="item"` use an
//    IntersectionObserver to add `.in` to the figure (CSS drives the paper-wipe
//    + image de-zoom) and `.show` to its `.strata` caption ~450ms later.
//
// 2. Legacy Liquid pages declare `data-controller="reveal"
//    data-reveal-animation-class="reveal"` on a wrapper and rely on raw <img>
//    children getting the animation class on scroll. We keep that path so PR 1+
//    chrome doesn't regress untouched pages.
export default class extends Controller {
  static targets = ["item"]
  static classes = ["animation"]

  connect() {
    this.reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false
    this.dbg = window.__estratosDebug || new URLSearchParams(location.search).has("debug")

    if (this.dbg) {
      console.log("[reveal] connect", {
        host: this.element,
        itemCount: this.itemTargets.length,
        reducedMotion: this.reduced,
        htmlClass: document.documentElement.className,
      })
    }

    if (this.hasItemTarget) {
      this.connectSequence()
    } else {
      this.connectLegacy()
    }
  }

  disconnect() {
    if (this.observer) this.observer.disconnect()
    if (this.legacyHandler) window.removeEventListener("scroll", this.legacyHandler)
  }

  // --- new: sequence reveals --------------------------------------------------

  connectSequence() {
    if (this.reduced) {
      this.itemTargets.forEach((el) => {
        el.classList.add("in")
        el.querySelectorAll(".strata").forEach((s) => s.classList.add("show"))
      })
      return
    }

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return
        entry.target.classList.add("in")
        const strata = entry.target.querySelector(".strata")
        if (strata) setTimeout(() => strata.classList.add("show"), 450)
        this.observer.unobserve(entry.target)
      })
    }, { threshold: 0.12 })

    // Per-item delay so items visible at first paint don't all snap in at
    // the same instant (the "pop"). Capped so the last item still feels
    // responsive. Items revealed by scroll naturally stagger as they enter.
    this.itemTargets.forEach((el, i) => {
      el.style.setProperty("--in-delay", `${Math.min(i, 8) * 60}ms`)
      this.observer.observe(el)
    })
  }

  // --- legacy: scroll-driven image class -------------------------------------

  connectLegacy() {
    if (this.reduced) {
      const cls = this.animationClass
      this.element.querySelectorAll(`img:not(.${cls})`).forEach((img) => img.classList.add(cls))
      return
    }

    this.legacyHandler = this.legacyReveal.bind(this)
    window.addEventListener("scroll", this.legacyHandler, { passive: true })
    this.legacyReveal()
  }

  legacyReveal() {
    const cls = this.animationClass
    const viewport = window.innerHeight
    this.element.querySelectorAll(`img:not(.${cls})`).forEach((img) => {
      if (img.getBoundingClientRect().top < viewport) img.classList.add(cls)
    })
  }
}
